// /assets/all

import { Asset, AssetType, PaginatedResponse, RwaStatus } from '@/types';
import { apiSlice } from './apiSlice';
export interface PaginatedResponseAssets<T> {
  data: T[];
  count: number;
  skip: number;
  take: number;
}

export interface AssetPayload {
  skip?: number;
  take?: number;
  search?: string;
  rwaStatus?: RwaStatus;
  subType?: string;
  assetStatus?: string;
  soldout?: boolean;
  newlyAdded?: boolean;
  available?: boolean;
  isVisible?: boolean;
  assetType?: AssetType;
  fromDate?: string;
  toDate?: string;
}

export interface UpdateAssetPayload {
  id: number;
  titleStatus: string;
  builtupArea: string;
  landArea: string;
  yearOfConstruction: number;
  assetAge?: number | undefined;
  marketValue: string;
  rentalIncomeMonthly: string;
  leaseTenureRemainingMonths: number;
  last3YearRevenue: string;
  initialPricePerFraction: number;
}

export const assetsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllAssets: builder.query<PaginatedResponseAssets<Asset>, AssetPayload>({
      query: ({
        skip = 0,
        take = 10,
        search,
        subType,
        soldout,
        newlyAdded,
        available,
        assetStatus,
        rwaStatus,
        assetType,
        fromDate,
        toDate,
      }) => ({
        url: '/assets',
        params: {
          skip,
          take,
          assetStatus,
          rwaStatus,
          assetType,
          fromDate,
          toDate,
          ...(search ? { search } : {}),
          ...(subType ? { subType } : {}),
          ...(soldout !== undefined ? { soldout } : {}),
          ...(newlyAdded !== undefined ? { newlyAdded } : {}),
          ...(available !== undefined ? { available } : {}),
        },
      }),
      providesTags: ['Assets'],
    }),
    getAssetById: builder.query<Asset, number>({
      query: (assetId) => `/assets/${assetId}`,
      providesTags: ['Assets'],
    }),
    getUserInvestedAssets: builder.query<
      PaginatedResponse<Asset>,
      {
        skip?: number;
        take?: number;
        search?: string;
        accountId?: string;
      }
    >({
      query: ({ skip = 0, take = 10, search, accountId }) => ({
        url: '/asset-purchase/me',
        params: {
          skip,
          take,
          accountId,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ['Assets'],
    }),
    getUserUploadedAssets: builder.query<
      PaginatedResponse<Asset>,
      {
        skip?: number;
        take?: number;
        search?: string;
        accountId?: string;
      }
    >({
      query: ({ skip = 0, take = 10, search, accountId }) => ({
        url: '/assets/admin',
        params: {
          skip,
          take,
          accountId,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ['Assets'],
    }),
    fractionalizeAsset: builder.mutation<
      void,
      {
        id: number;
        totalFractions: number;
        pricePerFraction: number;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/assets/${id}/fractionalize`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Assets'],
    }),
    updateAsset: builder.mutation<void, UpdateAssetPayload>({
      query: ({ id, ...body }) => ({
        url: `/assets/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Assets'],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetAllAssetsQuery,
  useGetAssetByIdQuery,
  useGetUserInvestedAssetsQuery,
  useGetUserUploadedAssetsQuery,
  useFractionalizeAssetMutation,
  useUpdateAssetMutation,
} = assetsApi;
